import React, { useEffect, useContext, useState } from 'react'
import LoadingOverlay from 'react-loading-overlay'
import { Card, CardBody, Table, Button, Modal } from 'reactstrap'
import ConfigContext from './ConfigContext'
import Success from './Success'
import Error from 'pages/NotificationModal/Error'

const ConfigDetail = ({ configType, onRuleEdit }) => {
    const {
        loading,
        success,
        error,
        setSuccess,
        setError,
        configData,
        rawConfigData,
        parseError,
        loadConfigData,
        updateResponse,
        getFieldMappings,
        getDefaultVarsForConfigType
    } = useContext(ConfigContext)

    const [showJson, setShowJson] = useState(false)

    // Pending Approval Config is being migrated to the Rules Engine, so show it read-only.
    const isMigrating = configType?.type === 'APPT_REQ_ORD_PROC_CFG'

    useEffect(() => {
        if (configType) {
            loadConfigData(configType.type)
        }
    }, [configType, success])

    const formatRuleValue = (rule, configTypeKey) => {
        // Handle literal/always types without rules
        if ((rule.type === 'LIT') || (rule.type === 'ALWAYS' && !rule.rules)) {
          // Handle all possible boolean formats for backward compatibility
          return (rule.value === 'FALSE' || rule.value === 'False' || rule.value === false) ? 'False' : 'True'
        }

        // Handle EXPR rules without proper rules structure - show empty
        if (rule.type === 'EXPR' && !rule.rules) {
          return ''
        }

        // Handle conditional rules (both old ALWAYS format and new EXPR format with rules)
        if ((rule.type === 'EXPR' && rule.rules) || (rule.type === 'ALWAYS' && rule.rules)) {
          const sections = []
          const fieldMappings = getFieldMappings(configTypeKey)
        
          if (rule.rules.true_conditions?.any?.length > 0) {
            const trueGroups = []
            rule.rules.true_conditions.any.forEach(anyGroup => {
              const conditions = []
              anyGroup.all?.forEach(condition => {
                const frontendField = Object.keys(fieldMappings).find(
                  key => fieldMappings[key] === condition.field
                ) || condition.field

                if (condition.op === 'in') {
                  let values = condition.value
                  if (typeof values === 'string' && values.includes(',')) {
                    values = values.split(',').map(v => v.trim()).filter(v => v !== '')
                  } else if (!Array.isArray(values)) {
                    values = [values]
                  }
                  const valueText = values.join(', ')
                  conditions.push(`${frontendField} in ${valueText}`)
                } else if (condition.op === '=') {
                  conditions.push(`${frontendField} is ${condition.value}`)
                } else if (condition.op === '!=') {
                  conditions.push(`${frontendField} is not ${condition.value}`)
                } else {
                  conditions.push(`${frontendField} ${condition.op} ${condition.value}`)
                }
              })

              if (conditions.length > 0) {
                trueGroups.push(conditions.join(' AND '))
              }
            })

            if (trueGroups.length > 0) {
              sections.push({
                type: 'true',
                text: trueGroups.join(' OR ')
              })
            }
          }

          if (rule.rules.false_conditions?.any?.length > 0) {
            const falseGroups = []
            rule.rules.false_conditions.any.forEach(anyGroup => {
              const conditions = []
              anyGroup.all?.forEach(condition => {
                const frontendField = Object.keys(fieldMappings).find(
                  key => fieldMappings[key] === condition.field
                ) || condition.field

                if (condition.op === 'in') {
                  let values = condition.value
                  if (typeof values === 'string' && values.includes(',')) {
                    values = values.split(',').map(v => v.trim()).filter(v => v !== '')
                  } else if (!Array.isArray(values)) {
                    values = [values]
                  }
                  const valueText = values.join(', ')
                  conditions.push(`${frontendField} in ${valueText}`)
                } else if (condition.op === '=') {
                  conditions.push(`${frontendField} is ${condition.value}`)
                } else if (condition.op === '!=') {
                  conditions.push(`${frontendField} is not ${condition.value}`)
                } else {
                  conditions.push(`${frontendField} ${condition.op} ${condition.value}`)
                }
              })

              if (conditions.length > 0) {
                falseGroups.push(conditions.join(' AND '))
              }
            })

            if (falseGroups.length > 0) {
              sections.push({
                type: 'false',
                text: falseGroups.join(' OR ')
              })
            }
          }

          return sections
        }

        return rule.value
    }

    const getRuleType = (rule) => {
        // EXPR type should always show as Conditional, regardless of rules property
        if (rule.type === 'EXPR' || (rule.type === 'ALWAYS' && rule.rules)) {
            return 'Conditional'
        }
        if (rule.type === 'LIT' || (rule.type === 'ALWAYS' && !rule.rules)) {
            return 'Always'
        }
        return 'Always' // Default fallback
    }


    const closeSuccess = () => {
        setSuccess(false)
    }

    const togError = () => {
        setError(false)
    }

    return (
        <>
            <Modal isOpen={success} toggle={closeSuccess} centered size="md">
                <Success
                    data={updateResponse}
                    cancel={closeSuccess}
                />
            </Modal>

            <Modal isOpen={error} toggle={togError} centered size="sm">
                <Error
                    message="Something went wrong"
                    closeModal={togError}
                />
            </Modal>

            <LoadingOverlay active={loading} spinner>
                <Card className='p-3'>
                    <CardBody className="p-0">
                        <Card className="mt-2 mb-3">
                            <CardBody className="p-3">
                                {isMigrating && (
                                    <div className="alert alert-warning d-flex align-items-center" role="alert" style={{ fontSize: '13px' }}>
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        <span>This configuration is being migrated to the Rules Engine. It is read-only here — editing is disabled.</span>
                                    </div>
                                )}
                                {configData && configData.vars && configData.vars.length > 0 ? (
                                    <div className="react-table">
                                        <Table bordered hover responsive>
                                            <thead className="table-light table-nowrap" style={{ 
                                                fontFamily: 'Inter, Poppins, sans-serif',
                                                fontSize: '13px', 
                                                lineHeight: '19.5px' 
                                            }}>
                                                <tr>
                                                    <th>
                                                        <div className="mb-2" style={{ fontWeight: '600' }}>
                                                            Customization
                                                        </div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-2" style={{ fontWeight: '600' }}>
                                                            Rule Type
                                                        </div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-2" style={{ fontWeight: '600' }}>
                                                            Value/Condition
                                                        </div>
                                                    </th>
                                                    <th>
                                                        <div className="mb-2" style={{ fontWeight: '600' }}>
                                                            Actions
                                                        </div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {configData.vars.map((rule, index) => (
                                                    <tr key={rule.name || index}>
                                                        <td>{(() => {
                                                            // Always use standard names from metadata, not corrupted DB notes
                                                            const defaultVars = getDefaultVarsForConfigType(configType?.type)
                                                            const standardName = defaultVars?.find(v => v.name === rule.name)?.notes
                                                            return standardName || rule.notes || rule.name
                                                        })()}</td>
                                                        <td>
                                                          <span className={`badge ${getRuleType(rule) === 'Literal' ? 'bg-secondary' : ''}`} 
                                                                style={{
                                                                  backgroundColor: getRuleType(rule) === 'Literal' ? '#6c757d' : '#EE5A29',
                                                                  color: 'white',
                                                                  fontSize: '11px',
                                                                  padding: '4px 8px'
                                                                }}>
                                                            {getRuleType(rule)}
                                                          </span>
                                                        </td>
                                                        <td className="text-break" style={{maxWidth: '400px'}}>
                                                          {rule.type === 'LIT' ? (
                                                            <div style={{
                                                              fontSize: '14px',
                                                              fontWeight: '500',
                                                              color: '#495057'
                                                            }}>
                                                              {formatRuleValue(rule, configType?.type)}
                                                            </div>
                                                          ) : (
                                                            <div style={{
                                                              display: 'flex',
                                                              flexDirection: 'column',
                                                              gap: '12px'
                                                            }}>
                                                              {(() => {
                                                                const ruleValue = formatRuleValue(rule, configType?.type)
                                                                if (Array.isArray(ruleValue)) {
                                                                  return (
                                                                    <>
                                                                      {ruleValue.map((section, index) => (
                                                                        <div key={index} style={{
                                                                          padding: '8px 12px',
                                                                          borderRadius: '8px',
                                                                          backgroundColor: '#f8f9fa',
                                                                          border: `2px solid #EE5A29`,
                                                                          borderLeft: `6px solid #EE5A29`
                                                                        }}>
                                                                          <div style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '8px',
                                                                            marginBottom: '4px'
                                                                          }}>
                                                                            <div style={{
                                                                              width: '8px',
                                                                              height: '8px',
                                                                              borderRadius: '50%',
                                                                              backgroundColor: '#EE5A29'
                                                                            }}></div>
                                                                            <span style={{
                                                                              fontSize: '11px',
                                                                              fontWeight: 'bold',
                                                                              color: '#EE5A29',
                                                                              textTransform: 'uppercase',
                                                                              letterSpacing: '0.5px'
                                                                            }}>
                                                                              {section.type === 'true' ? 'True When' : 'False When'}
                                                                            </span>
                                                                          </div>

                                                                          <div style={{
                                                                            fontSize: '13px',
                                                                            color: '#495057',
                                                                            lineHeight: '1.4',
                                                                            marginLeft: '20px'
                                                                          }}>
                                                                            {section.text.split(' OR ').map((group, groupIndex) => (
                                                                              <div key={groupIndex} style={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                flexWrap: 'wrap',
                                                                                gap: '6px',
                                                                                marginBottom: groupIndex < section.text.split(' OR ').length - 1 ? '8px' : '0'
                                                                              }}>
                                                                                {groupIndex > 0 && (
                                                                                  <span style={{
                                                                                    fontSize: '10px',
                                                                                    fontWeight: 'bold',
                                                                                    color: '#6c757d',
                                                                                    backgroundColor: '#e9ecef',
                                                                                    padding: '2px 6px',
                                                                                    borderRadius: '4px'
                                                                                  }}>OR</span>
                                                                                )}

                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                                  {group.split(' AND ').map((condition, condIndex) => (
                                                                                    <React.Fragment key={condIndex}>
                                                                                      {condIndex > 0 && (
                                                                                        <span style={{
                                                                                          fontSize: '10px',
                                                                                          fontWeight: 'bold',
                                                                                          color: '#495057',
                                                                                          margin: '0 4px'
                                                                                        }}>AND</span>
                                                                                      )}
                                                                                      <span style={{
                                                                                        fontSize: '12px',
                                                                                        fontWeight: '500',
                                                                                        color: '#495057',
                                                                                        fontStyle: 'italic'
                                                                                      }}>
                                                                                        {condition}
                                                                                      </span>
                                                                                    </React.Fragment>
                                                                                  ))}
                                                                                </div>
                                                                              </div>
                                                                            ))}
                                                                          </div>
                                                                        </div>
                                                                      ))}
                                                                      <div style={{
                                                                        fontSize: '12px',
                                                                        color: '#EE5A29',
                                                                        textAlign: 'left',
                                                                        marginTop: '4px',
                                                                        fontWeight: '500'
                                                                      }}>
                                                                        {ruleValue.some(section => section.type === 'true')
                                                                          ? 'False otherwise'
                                                                          : 'True otherwise'}
                                                                      </div>
                                                                    </>
                                                                  )
                                                                } else {
                                                                  return (
                                                                    <div style={{
                                                                      fontSize: '14px',
                                                                      fontWeight: '500',
                                                                      color: '#495057'
                                                                    }}>
                                                                      {ruleValue}
                                                                    </div>
                                                                  )
                                                                }
                                                              })()}
                                                            </div>
                                                          )}
                                                        </td>
                                                        <td>
                                                        <Button
                                                            color="primary"
                                                            style={{
                                                              fontSize: '12px',
                                                              padding: '4px 12px'
                                                            }}
                                                            size="sm"
                                                            disabled={isMigrating}
                                                            title={isMigrating ? 'Being migrated to the Rules Engine — read-only' : undefined}
                                                            onClick={() => { if (!isMigrating) onRuleEdit(rule) }}
                                                        >
                                                            Edit Rule
                                                        </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                        <h6>No configuration rules found</h6>
                                        <p className="text-muted">This configuration type has no rules defined yet.</p>
                                    </div>
                                )}

                                {/* Parse Error Warning */}
                                {parseError && (
                                    <div className="alert alert-warning mt-3" role="alert">
                                        <div className="d-flex align-items-center">
                                            <i className="fas fa-exclamation-triangle me-2"></i>
                                            <div>
                                                <strong>Configuration Format Issue Detected</strong>
                                                <p className="mb-0 mt-1">The existing configuration format has compatibility issues. Default variables are shown above. You can edit and save them to update the configuration.</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* JSON Expansion Section */}
                                <div className='mt-4'>
                                    <div className='d-flex align-items-center gap-2 mb-2' style={{cursor: 'pointer'}} onClick={() => setShowJson(!showJson)}>
                                        <h6 className='mb-0 text-secondary'>Expand for JSON</h6>
                                        <i className={`fas fa-caret-${showJson ? 'up' : 'down'} text-secondary`} style={{ color: '#6c757d', fontSize: '15px' }}></i>
                                    </div>
                                    {showJson && (
                                        <div style={{backgroundColor: '#f8f9fa', border:'1px solid #dee2e6', borderRadius:'5px', padding: '15px', fontFamily: 'monospace', fontSize: '0.875rem', maxHeight:'400px', overflowY:'auto'}}>
                                            <pre>{JSON.stringify(rawConfigData?.config || {}, null, 2)}</pre>
                                        </div>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </CardBody>
                </Card>
            </LoadingOverlay>
        </>
    )
}

export default ConfigDetail